import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';

import { HeaderComponent } from '../common/header/header.component';
import { SidebarComponent } from '../common/slidebar/sidebar.components';
import { ContactDirectoryService } from '../../services/contact-directory/contact-directory.service';
import { ContactFormDialogComponent } from './contact-form-dialog/contact-form-dialog.component';

@Component({
  selector: 'app-contact-directory',
  imports: [
    HeaderComponent,
    CommonModule,
    RouterLink,
    FormsModule,
    SidebarComponent,
  ],
  templateUrl: './contact-directory.component.html',
  styleUrl: './contact-directory.component.css',
})
export class ContactDirectoryComponent implements OnInit, OnDestroy {
  isSubmissionSuccess: boolean = false;
  searchQuery: string = '';
  activeFilters: string[] = [];
  // UI-only: keep operator type selection consistent with the new template (single-select).
  // The backend still receives `activeFilters` as `column_filters`.
  selectedOperatorType: string = 'ALL';
  selectedOrg: any | null = null;
  startDate: string = '';
  endDate: string = '';
  sortOrder: 'ASC' | 'DESC' = 'DESC';

  organizations: any[] = [];
  isLoading: boolean = false;
  isLoadingMore: boolean = false;
  operatorTypes: any[] = [];

  // Directory stats (top cards)
  allOperatorsCount: number = 0;
  ispOperatorsCount: number = 0;
  bankOperatorsCount: number = 0;
  ewalletOperatorsCount: number = 0;

  // Infinite scroll properties
  currentPage: number = 1;
  itemsPerPage: number = 20;
  totalItems: number = 0;
  totalPages: number = 1;
  hasMoreItems: boolean = true;
  // Uses -1 as an ellipsis sentinel for the pagination UI.
  paginationItems: number[] = [];

  constructor(
    private contactDirectoryService: ContactDirectoryService,
    private dialog: MatDialog,
    private toastr: ToastrService,
  ) {}

  // Matches the design file's operator color mapping.
  private readonly operatorTypeColorHex: Record<string, string> = {
    'ISP': '#3b82f6',
    'BANK': '#f59e0b',
    'E-COMMERCE': '#22c55e',
    'E-WALLET': '#a855f7',
    'SOCIAL MEDIA PLATFORM': '#ef4444',
    'TELECOM SERVICE PROVIDER': '#14b8a6',
    'OTHER SERVICE PROVIDER': '#94a3b8',
    'EMAIL SERVICE PROVIDER': '#38bdf8',
    'OTT SERVICE PROVIDER': '#db2777',
    'DEFENCE': '#64748b',
    'INSURANCE': '#10b981',
  };

  private readonly operatorTypeBgLight: Record<string, string> = {
    'ISP': 'rgba(59,130,246,.08)',
    'BANK': 'rgba(245,158,11,.08)',
    'E-COMMERCE': 'rgba(34,197,94,.08)',
    'E-WALLET': 'rgba(168,85,247,.08)',
    'SOCIAL MEDIA PLATFORM': 'rgba(239,68,68,.08)',
    'TELECOM SERVICE PROVIDER': 'rgba(20,184,166,.08)',
    'OTHER SERVICE PROVIDER': 'rgba(148,163,184,.08)',
    'EMAIL SERVICE PROVIDER': 'rgba(56,189,248,.08)',
    'OTT SERVICE PROVIDER': 'rgba(219,39,119,.08)',
    'DEFENCE': 'rgba(100,116,139,.08)',
    'INSURANCE': 'rgba(16,185,129,.08)',
  };

  private readonly operatorTypeBadgeClass: Record<string, string> = {
    'ISP': 'badge-isp',
    'BANK': 'badge-isp',
    'E-COMMERCE': 'badge-ecommerce',
    'E-WALLET': 'badge-ewallet',
  };

  private readonly operatorTypeIconClass: Record<string, string> = {
    'ISP': 'fas fa-wifi',
    'BANK': 'fas fa-landmark',
    'E-COMMERCE': 'fas fa-shopping-cart',
    'E-WALLET': 'fas fa-wallet',
    'SOCIAL MEDIA PLATFORM': 'fas fa-users',
    'TELECOM SERVICE PROVIDER': 'fas fa-satellite-dish',
    'OTHER SERVICE PROVIDER': 'fas fa-building',
    'EMAIL SERVICE PROVIDER': 'fas fa-envelope',
    'OTT SERVICE PROVIDER': 'fas fa-tv',
    'DEFENCE': 'fas fa-shield-alt',
    'INSURANCE': 'fas fa-file-invoice-dollar',
  };

  ngOnInit() {
    this.loadOperatorTypes();
    this.loadMasterData(true);
  }

  loadOperatorTypes() {
    this.contactDirectoryService.getOperatorTypes().subscribe({
      next: (response: any) => {
        if (response && response.data) {
          // Handle different possible response structures
          if (Array.isArray(response.data)) {
            this.operatorTypes = response.data;
          } else if (
            response.data.operator_types &&
            Array.isArray(response.data.operator_types)
          ) {
            this.operatorTypes = response.data.operator_types;
          } else if (response.data.rows && Array.isArray(response.data.rows)) {
            this.operatorTypes = response.data.rows;
          } else {
            this.operatorTypes = [];
          }
        } else {
          this.operatorTypes = [];
        }
      },
      error: (error: any) => {
        console.error('Error loading operator types:', error);
        this.operatorTypes = [];
      },
    });
  }

  loadMasterData(reset: boolean = false) {
    return this.loadMasterDataInternal(reset, true);
  }

  /**
   * Loads directory list (paginated) and optionally updates top-card stats.
   * `shouldUpdateStats=false` is used when changing operator type filter so that
   * stat cards keep stable overall counts.
   */
  private loadMasterDataInternal(
    reset: boolean = false,
    shouldUpdateStats: boolean = true,
  ) {
    if (reset) {
      this.currentPage = 1;
      this.organizations = [];
    }
    // Pagination mode: always replace the current page results.
    this.isLoading = true;
    this.isLoadingMore = false;

    // Format dates as YYYY-MM-DD
    const formattedStartDate = this.startDate
      ? this.formatDateForAPI(this.startDate)
      : undefined;
    const formattedEndDate = this.endDate
      ? this.formatDateForAPI(this.endDate)
      : undefined;

    this.contactDirectoryService
      .getMasterData(
        this.currentPage,
        this.itemsPerPage,
        this.searchQuery,
        this.activeFilters.length > 0 ? this.activeFilters : undefined,
        formattedStartDate,
        formattedEndDate,
        'updated_on',
        this.sortOrder,
      )
      .subscribe({
        next: (response: any) => {
          if (response && response.data && response.data.rows) {
            const newOrganizations = this.mapApiResponseToOrganizations(
              response.data.rows,
            );

            this.organizations = newOrganizations;
            this.totalItems = response.data.count || 0;
            if (shouldUpdateStats) {
              // Keep overall count in sync for "All Operators" card.
              this.allOperatorsCount =
                response.data.operator_type_total ?? this.totalItems;
              const counts = response.data.operator_type_counts || {};
              this.ispOperatorsCount = counts['ISP'] ?? 0;
              this.bankOperatorsCount = counts['BANK'] ?? 0;
              this.ewalletOperatorsCount = counts['E-WALLET'] ?? 0;
            }

            this.totalPages = response.data.total_pages || 1;
            this.hasMoreItems = this.currentPage < this.totalPages;
            this.updatePaginationItems();
          } else {
            this.organizations = [];
            this.totalItems = 0;
            if (shouldUpdateStats) {
              this.allOperatorsCount = 0;
              this.ispOperatorsCount = 0;
              this.bankOperatorsCount = 0;
              this.ewalletOperatorsCount = 0;
            }
            this.totalPages = 1;
            this.hasMoreItems = false;
            this.updatePaginationItems();
          }
          this.isLoading = false;
          this.isLoadingMore = false;
        },
        error: (error) => {
          console.error('Error loading master data:', error);
          this.organizations = [];
          this.totalItems = 0;
          this.totalPages = 1;
          this.hasMoreItems = false;
          this.updatePaginationItems();
          this.isLoading = false;
          this.isLoadingMore = false;
        },
      });
  }

  // Click handler for the top stat cards.
  setOperatorTypeFromStats(typeKey: 'ALL' | string) {
    if (typeKey === 'ALL') {
      this.selectedOperatorType = 'ALL';
      this.activeFilters = [];
      this.loadMasterDataInternal(true, false);
      return;
    }

    this.selectedOperatorType = typeKey;
    this.activeFilters = [typeKey];
    this.loadMasterDataInternal(true, false);
  }

  mapApiResponseToOrganizations(rows: any[]): any[] {
    return rows.map((item: any) => {
      const rowData = item.row_data || {};
      const extraFields = rowData.extra_fields || {};

      // Extract phone from row_data.phone
      const phone = this.normalizePhoneList(rowData.phone) || null;

      // Uses `last_update` included in `/list-master-data` response.
      const lastUpdateEntry = this.mapLastUpdateToHistoryEntry(
        item.last_update,
      );
      const officerHistory = lastUpdateEntry ? [lastUpdateEntry] : [];

      // Format dates from ISO string to readable format
      const formatDate = (dateString: string | null | undefined): string => {
        if (!dateString) return '';
        try {
          const date = new Date(dateString);
          return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          });
        } catch {
          return dateString;
        }
      };

      return {
        name: rowData.operator || null, // Organization/Operator name
        type: rowData.operator_type || item.operator_type || null, // Operator type for badge
        officerName: rowData.name || null, // Nodal officer name
        email: rowData.email || null,
        phone: phone,
        address: rowData.address || null,
        createdOn: formatDate(item.created_on),
        updatedOn: formatDate(item.updated_on || null),
        sno: rowData.Sno || null,
        designation: rowData.designation || null,
        lastLogin: formatDate(rowData.last_login),
        lsaName: rowData.lsa_Name || null,
        areaCovered: rowData.area_covered || null,
        merchant: rowData.merchant || null,
        operatorType: rowData.operator_type || item.operator_type || null,
        operator: rowData.operator || null,
        operatorLogoUrl: item.operator_logo_url || null,
        contributerName:
          item.contributer_name || null,
        extraFields: extraFields,
        submissionId: item.submission_id || null,
        officerHistory,
      };
    });
  }

  private mapLastUpdateToHistoryEntry(lastUpdate: any): {
    officerName: string;
    designation: string;
    email: string;
    phone: string;
    replacedOn: string;
  } | null {
    if (!lastUpdate) return null;

    const status = (lastUpdate?.status ?? '').toString().toLowerCase();
    if (status !== 'approved') return null;

    const updated = lastUpdate?.previous_row_data || {};

    const normalizeMaybeArrayToString = (v: any): string => {
      if (v === null || v === undefined) return '';
      if (Array.isArray(v)) return v.join(', ');
      return v.toString();
    };

    const entry = {
      officerName: updated?.name || '',
      designation: updated?.designation || '',
      email: normalizeMaybeArrayToString(updated?.email),
      phone: this.normalizePhoneList(
        normalizeMaybeArrayToString(updated?.phone),
      ),
      replacedOn: this.formatDateForDisplay(
        lastUpdate?.reviewed_on ||
          lastUpdate?.updated_on ||
          lastUpdate?.created_on,
      ),
    };

    if (!(entry.officerName || entry.email || entry.phone)) return null;
    return entry;
  }

  private formatDateForDisplay(dateString: string | null | undefined): string {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return dateString;
    }
  }

  formatValue(value: string | null | undefined): string {
    if (value === null || value === undefined || value === '') {
      return 'N/A';
    }
    return value;
  }

  /**
   * Normalizes phone values for display/copy.
   * - keeps a leading `+` when present
   * - strips spaces and non-digit separators
   * - fixes duplicated country prefix patterns like +91919118421488 -> +919118421488
   */
  normalizePhoneList(value: any): string {
    if (value === null || value === undefined) return '';

    const raw = Array.isArray(value) ? value.join(',') : value.toString();
    const parts = raw
      .split(',')
      .map((part: string) => this.normalizeSinglePhone(part))
      .filter((part: string) => part.length > 0);

    return parts.join(', ');
  }

  private normalizeSinglePhone(value: any): string {
    const raw = (value ?? '').toString().trim();
    if (!raw) return '';

    const hasPlus = raw.startsWith('+');
    const digitsOnly = raw.replace(/\D/g, '');
    if (!digitsOnly) return '';

    // Common duplicate pattern for India numbers:
    // +91 + (again 91 before the 10-digit mobile) e.g. +91919118421488
    if (hasPlus && digitsOnly.startsWith('9191') && digitsOnly.length > 12) {
      const candidate = `91${digitsOnly.slice(4)}`;
      if (candidate.length === 12) {
        return `+${candidate}`;
      }
    }

    return hasPlus ? `+${digitsOnly}` : digitsOnly;
  }

  getExtraFieldKeys(extraFields: any): string[] {
    if (!extraFields || typeof extraFields !== 'object') {
      return [];
    }
    return Object.keys(extraFields);
  }

  openModal() {
    const dialogRef = this.dialog.open(ContactFormDialogComponent, {
      width: '520px',
      maxWidth: '95vw',
      disableClose: false,
      panelClass: 'contact-form-dialog',
      autoFocus: false,
      hasBackdrop: true,
      backdropClass: 'contact-form-dialog-backdrop',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.success) {
        // Show success screen
        this.isSubmissionSuccess = true;
        // Reload the data to show the latest entry
        this.loadMasterData(true);
      }
    });
  }

  /** Opens the contact form prefilled from the drawer row. Uses request-update API when submissionId exists (like My Contributions edit). */
  openEditContactDialog() {
    if (!this.selectedOrg) {
      return;
    }

    const org = this.selectedOrg;
    const toCommaSeparated = (value: any): string => {
      if (Array.isArray(value)) {
        return value
          .map((item) => (item ?? '').toString().trim())
          .filter((item) => item.length > 0)
          .join(', ');
      }
      return (value ?? '').toString().trim();
    };

    const prefillFromDirectory = {
      name: org.officerName || '',
      friendly_name: '',
      email: toCommaSeparated(org.email),
      phone: toCommaSeparated(org.phone),
      address: org.address || '',
      operator: org.operator || org.name || '',
      operator_type: org.type || org.operatorType || '',
    };

    // Use request-update API when we have submissionId (same as My Contributions edit)
    const hasSubmissionId = !!(
      org.submissionId && String(org.submissionId).trim()
    );
    const dialogData: any = {
      prefillFromDirectory,
    };
    if (hasSubmissionId) {
      dialogData.isEditMode = true;
      dialogData.submissionId = org.submissionId;
    }

    if (!hasSubmissionId) {
      this.toastr.warning(
        'This entry cannot be edited via update request. Submission ID not available.',
        'Edit not available',
      );
      return;
    }

    // Close drawer first — its z-index is above the dialog overlay, so the modal must open after unmount.
    this.closeDetailView();

    // Open after a tick so *ngIf can remove the drawer from the DOM before the overlay attaches.
    setTimeout(() => {
      const dialogRef = this.dialog.open(ContactFormDialogComponent, {
        width: '520px',
        maxWidth: '95vw',
        disableClose: false,
        panelClass: 'contact-form-dialog',
        autoFocus: false,
        hasBackdrop: true,
        backdropClass: 'contact-form-dialog-backdrop',
        data: dialogData,
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result && result.success) {
          if (result.isEdit) {
            this.loadMasterData(true);
          } else {
            this.isSubmissionSuccess = true;
            this.loadMasterData(true);
          }
        }
      });
    }, 0);
  }

  /** Share / copy summary for the open drawer row (UI-only). */
  shareContactFromDrawer() {
    if (!this.selectedOrg) {
      return;
    }
    const org = this.selectedOrg;
    const lines = [
      org.name,
      org.officerName,
      org.email,
      this.normalizePhoneList(org.phone),
      org.address,
    ]
      .map((v) => (v ?? '').toString().trim())
      .filter((v) => v.length > 0 && v !== 'N/A');
    const text = lines.join('\n');

    if (navigator.share && text) {
      navigator
        .share({ title: org.name || 'Contact', text })
        .catch(() => this.copyShareFallback(text));
    } else {
      this.copyShareFallback(text);
    }
  }

  private copyShareFallback(text: string) {
    if (!text) {
      this.toastr.info('Nothing to share');
      return;
    }
    if (!navigator.clipboard?.writeText) {
      this.toastr.error('Clipboard not available in this browser', 'Error');
      return;
    }
    navigator.clipboard
      .writeText(text)
      .then(() => this.toastr.success('Contact details copied'))
      .catch(() => this.toastr.error('Copy failed', 'Error'));
  }

  get officerHistoryPreviousCount(): number {
    return this.currentOfficerHistoryEntries.length;
  }

  get currentOfficerHistoryEntries(): {
    officerName: string;
    designation: string;
    email: string;
    phone: string;
    replacedOn: string;
  }[] {
    return (this.selectedOrg?.officerHistory ?? []) as any[];
  }

  get hasOfficerHistory(): boolean {
    return this.currentOfficerHistoryEntries.length > 0;
  }

  openDetailView(org: any) {
    this.selectedOrg = org;
  }

  closeDetailView() {
    this.selectedOrg = null;
  }

  copyToClipboard(text: any, event: Event) {
    event.stopPropagation();
    const value = (text ?? '').toString();
    if (!value || value === 'N/A') return;

    if (!navigator.clipboard?.writeText) {
      this.toastr.error('Clipboard not available in this browser', 'Error');
      return;
    }

    navigator.clipboard
      .writeText(value)
      .then(() => this.toastr.success('Copied!'))
      .catch(() => this.toastr.error('Copy failed', 'Error'));
  }

  private updatePaginationItems() {
    const total = Math.max(1, this.totalPages);
    const current = this.currentPage;

    if (total <= 7) {
      this.paginationItems = Array.from({ length: total }, (_, i) => i + 1);
      return;
    }

    const pages: number[] = [];
    pages.push(1);

    const left = Math.max(2, current - 1);
    const right = Math.min(total - 1, current + 1);

    if (left > 2) pages.push(-1);

    for (let p = left; p <= right; p++) {
      pages.push(p);
    }

    if (right < total - 1) pages.push(-1);

    pages.push(total);
    this.paginationItems = pages;
  }

  onScroll(event: Event) {
    if (this.isLoading || this.isLoadingMore || !this.hasMoreItems) {
      return;
    }

    const target = event.target as HTMLElement;
    if (!target) {
      return;
    }

    // Check if user has scrolled near the bottom of the container (within 200px)
    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;
    const scrollBottom = scrollTop + clientHeight;

    if (scrollBottom >= scrollHeight - 200) {
      this.loadMasterDataInternal(false, false);
    }
  }

  goToPage(page: number) {
    if (page < 1 || page > this.totalPages || page === this.currentPage) {
      return;
    }
    this.selectedOrg = null;
    this.currentPage = page;
    this.loadMasterDataInternal(false, false);
  }

  toggleFilter(filterType: string, event: Event) {
    event.preventDefault();
    const index = this.activeFilters.indexOf(filterType);

    if (index > -1) {
      this.activeFilters.splice(index, 1);
    } else {
      this.activeFilters.push(filterType);
    }

    // Reset and reload when filters change
    this.loadMasterDataInternal(true, false);
  }

  removeFilter(filter: string) {
    const index = this.activeFilters.indexOf(filter);
    if (index > -1) {
      this.activeFilters.splice(index, 1);
      // Reset and reload when filters change
      this.loadMasterDataInternal(true, false);
    }
  }

  onOperatorTypeChange() {
    // The UI is single-select; enforce a single value in `activeFilters`.
    if (!this.selectedOperatorType || this.selectedOperatorType === 'ALL') {
      this.activeFilters = [];
    } else {
      this.activeFilters = [this.selectedOperatorType];
    }
    // Operator type dropdown should NOT affect the stat cards.
    this.loadMasterDataInternal(true, false);
  }

  resetFilters() {
    this.searchQuery = '';
    this.selectedOperatorType = 'ALL';
    this.activeFilters = [];
    this.startDate = '';
    this.endDate = '';
    // Keep current default sort order (UI may not expose it anymore).
    this.sortOrder = 'DESC';
    this.loadMasterDataInternal(true, false);
  }

  onSearch() {
    // Don't make API call if search query is empty or just whitespace
    if (!this.searchQuery || this.searchQuery.trim() === '') {
      return;
    }
    // Reset and reload when searching
    this.loadMasterDataInternal(true, false);
  }

  onSearchInput() {
    // If search is cleared, reset and reload
    if (!this.searchQuery || this.searchQuery.trim() === '') {
      this.loadMasterDataInternal(true, false);
    }
  }

  getOperatorTypeHex(type: any): string {
    return this.operatorTypeColorHex[type] || '#94a3b8';
  }

  getOperatorTypeBgLight(type: any): string {
    return this.operatorTypeBgLight[type] || 'rgba(148,163,184,.08)';
  }

  getOperatorTypeBadgeClass(type: any): string {
    return this.operatorTypeBadgeClass[type] || 'badge-ecommerce';
  }

  getOperatorTypeIconClass(type: any): string {
    return this.operatorTypeIconClass[type] || 'fas fa-layer-group';
  }

  getCardTopGradient(type: any): string {
    const color = this.getOperatorTypeHex(type);
    return `linear-gradient(90deg, ${color}, transparent)`;
  }

  getInitials(value: any): string {
    const name = (value ?? '').toString().trim();
    if (!name) return '';
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase();
    }
    return `${(parts[0][0] ?? '').toUpperCase()}${(parts[1][0] ?? '').toUpperCase()}`;
  }

  getResultsInfoText(): string {
    const loaded = this.organizations.length;
    if (loaded === 0) return 'No operators found';
    const total = this.totalItems || loaded;
    return `Showing 1-${loaded} of ${total} operators`;
  }

  ngOnDestroy() {
    // Cleanup if needed
  }

  backToDirectory() {
    this.isSubmissionSuccess = false;
    // Reload the data to ensure latest entry is shown
    this.loadMasterData(true);
  }

  getOperatorTypeName(operatorType: any): string {
    // Handle different possible structures from API
    if (typeof operatorType === 'string') {
      return operatorType;
    }
    return (
      operatorType?.name ||
      operatorType?.operator_type ||
      operatorType?.type ||
      operatorType?.value ||
      'N/A'
    );
  }

  formatDateForAPI(dateString: string): string {
    // Convert date string to YYYY-MM-DD format
    if (!dateString) {
      return '';
    }
    try {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch {
      return dateString;
    }
  }

  onDateChange() {
    // Validate that both dates are selected or both are empty
    const hasStartDate = !!this.startDate;
    const hasEndDate = !!this.endDate;

    if (hasStartDate && !hasEndDate) {
      this.toastr.warning('Please select both Start Date and End Date');
      return;
    }

    if (hasEndDate && !hasStartDate) {
      this.toastr.warning('Please select both Start Date and End Date');
      return;
    }

    // Reset and reload when dates change (only if both are selected or both are empty)
    this.loadMasterDataInternal(true, false);
  }

  toggleSort() {
    // Don't sort if there are no organizations to sort
    if (this.organizations.length === 0 && !this.isLoading) {
      return;
    }
    // Toggle between DESC and ASC
    this.sortOrder = this.sortOrder === 'DESC' ? 'ASC' : 'DESC';
    // Reset and reload when sort changes
    this.loadMasterDataInternal(true, false);
  }
}
